import type {Repository} from '@github-ui/current-repository'
import type {NavigationUrls} from '../../types/navigation-urls-types'
import type {MergeabilityLiveUpdateChannels} from '@github-ui/mergebox/use-mergeability-live-updates'
import type {MergeMethod} from '@github-ui/mergebox/types'
import type {PullRequest, PullRequestState} from '../../types'
import type {PendingReview} from '../../stores/slices/pending-review'
import type {PullRequestNotices, UserNotice} from './notices'
import type {DiffstatResponse} from '@github-ui/dotcom-schema/types/pull_requests/v1/diffstat'

export type OnboardingBannerProps = {
  dismissNoticePath: string
  helpURL: string
  repoSettingsPath: string
  showOptOut: boolean
}

export type DependabotAutomatedSecurityUpdates = {
  render: boolean
  alertPresent: boolean
  packageName: string
  singleAlert: boolean
  securityAlertPath: string
  severity: string
  showOnboardingPopover: boolean
  onboardingBannerProps: OnboardingBannerProps
}

export type ReviewRequestBanner =
  | {
      render: false
    }
  | {
      render: true
      requestedBySelf: boolean
      requestingUserDisplayLogin: string
    }

type BannersType = {
  banners: {
    dependabotAutomatedSecurityUpdates: DependabotAutomatedSecurityUpdates
    pausedDependabotUpdate: {render: boolean}
    hiddenCharacterWarning: {render: boolean}
    reviewRequestBanner: ReviewRequestBanner
    canStackBanner: {render: boolean}
  }
}

export type MergeStatusButtonData = {
  channels: MergeabilityLiveUpdateChannels | null
  defaultMergeMethod: MergeMethod
  headSha: string
  headShaChannel: string
  helpUrl: string
  viewerLogin?: string
}

export type AsyncStackData = {
  id: number
  stack: StackData
}

export type StackData = {
  allPullsLocked?: boolean
  baseBranch: string
  createdAt: string
  id: number
  number: number
  repositoryOwnerLogin: string
  repositoryName: string
  position: number
  size: number
  pulls: Array<{
    headBranch: string
    isUnstackable: boolean
    title: string
    number: number
    state: PullRequestState
    url: string
  }>
}

export type AliveChannels = {
  pullRequest: string
  pullRequestState: string
  pullRequestTimeline: string
}

export type ViewSettings = {
  dockedPanelWidth: number | null
}

export type HeaderPageData = {
  aliveChannels: AliveChannels
  pageTitle: string
  bannersData: BannersType
  featureStates?: FeatureStates
  helpUrl: string
  pullRequest: PullRequest
  repository: HeaderRepository
  urls: NavigationUrls
  user: User
  mergeStatusButtonData?: MergeStatusButtonData
  stack?: StackData | null
  viewerPendingReview?: PendingReview
  viewSettings: ViewSettings
}

export type DiffstatData = DiffstatResponse

export type HeaderRepository = Repository & {
  codespacesEnabled: boolean
  isEnterprise: boolean
  editorEnabled: boolean
  userNameDisplayConfiguration?: string | null
}

export interface User {
  canChangeBase: boolean
  canComment?: boolean
  canEditTitle: boolean
  notices?: Array<UserNotice<PullRequestNotices>>
}

export const FeatureState = {
  Enabled: 'ENABLED',
  Available: 'AVAILABLE',
  Unavailable: 'UNAVAILABLE',
} as const

export type FeatureState = (typeof FeatureState)[keyof typeof FeatureState]

export interface FeatureStates {
  overview?: FeatureState
  pullRequestStacks?: FeatureState
  pullRequestStacksStatusesV2?: FeatureState
  pullRequestStacksPreventUnstackingLockedPrs?: FeatureState
  pullRequestStacksUpdateMergeConflictAndRebaseUi?: FeatureState
}
